import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { navBarHeight } from '../../Constants';
import type { Theme} from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import type { ApiContextProps } from '../../Contexts/ApiContext';
import ApiContext from '../../Contexts/ApiContext';
import type { TokenContextProps } from '../../Contexts/TokenContext';
import TokenContext from '../../Contexts/TokenContext';
import type { NavigateFunction} from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useTitle } from 'shouldve-been-react';
import OrderInfoSubTab from './OrderInfoSubTab';
import type Order from '../../Models/OrderModel';
import { datetimeToVisualString } from '../../Utils/DateTimeParser';
import type StatusDate from '../../Models/StatusDateModel';
import StatusDateType from '../../Enums/StatusDateType';
import ButtonGroup from '@mui/material/ButtonGroup';
import Button from '@mui/material/Button';
import type { LocalStorageContextProps } from '../../Contexts/LocalStorageContext';
import LocalStorageContext from '../../Contexts/LocalStorageContext';
import type { SettingsContextProps } from '../../Contexts/SettingsContext';
import SettingsContext from '../../Contexts/SettingsContext';
import VesselFilteringDialog from '../../Dialogs/OrderDialogs/VesselFilteringDialog';
import type Vessel from '../../Models/VesselModel';
import IconButton from '@mui/material/IconButton';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFilter, faSort } from '@fortawesome/free-solid-svg-icons';
import VesselSortingDialog from '../../Dialogs/OrderDialogs/VesselSortingDialog';
import { LoadDataBackdrop } from 'shouldve-been-mui';

type OrdersTabProps = {
    openedOrderId: string | null,
};

function OrdersTab(props: Readonly<OrdersTabProps>): JSX.Element {

    useTitle('K-Fleet Purchase - Orders');

    const apiContext: ApiContextProps = useContext<ApiContextProps>(ApiContext);
    const tokenContext: TokenContextProps = useContext<TokenContextProps>(TokenContext);
    const localStorageContext: LocalStorageContextProps = useContext<LocalStorageContextProps>(LocalStorageContext);
    const settingsContext: SettingsContextProps = useContext<SettingsContextProps>(SettingsContext);

    const currentTheme: Theme = useTheme();

    const navigate: NavigateFunction = useNavigate();

    const [orders, setOrders] = useState<Order[]>([]);
    const [statusDates, setStatusDates] = useState<StatusDate[]>([]);

    const [orderMode, setOrderMode] = useState<'reqApproval' | 'orderApproval'>('orderApproval');
    const [activeSort, setActiveSort] = useState<string>('orderNo');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
    const [vesselIdsFiltering, setVesselIdsFiltering] = useState<number[]>([]);
    const [showVesselFilteringDialog, setShowVesselFilteringDialog] = useState<boolean>(false);
    const [showVesselSortingDialog, setShowVesselSortingDialog] = useState<boolean>(false);
    const [vessels, setVessels] = useState<Vessel[]>([]);

    const [ordersIsLoaded, setOrdersIsLoaded] = useState<boolean>(false);
    const [statusDatesIsLoaded, setStatusDatesIsLoaded] = useState<boolean>(false);
    const [vesselsIsLoaded, setVesselsIsLoaded] = useState<boolean>(false);

    const [rerenderFlag, setRerenderFlag] = useState<Date>(new Date());
    const [localStorageRerenderFlag, setLocalStorageRerenderFlag] = useState<Date>(new Date());
    const [windowSize, setWindowSize] = useState<[number, number]>([1920, 1080]);

    const openedOrder: Order | null = useMemo<Order | null>(() => orders.find((order: Order) => order.orderId === props.openedOrderId) ?? null, [orders, props.openedOrderId]);

    const canShowReqApproval: boolean = useMemo<boolean>(() => statusDates.find((statusDate: StatusDate) => statusDate.fieldName === StatusDateType.REQAPPROVED)?.visible ?? false, [statusDates]);
    const canApproveReadyOnly: boolean = useMemo<boolean>(() => statusDates.find((statusDate: StatusDate) => statusDate.fieldName === StatusDateType.WAPPROVE)?.visible ?? false, [statusDates]);
    const canPostpone: boolean = useMemo<boolean>(() => statusDates.find((statusDate: StatusDate) => statusDate.fieldName === StatusDateType.POSTPONED)?.visible ?? false, [statusDates]);
    const canRequestVesselToReapprove: boolean = useMemo<boolean>(() => statusDates.find((statusDate: StatusDate) => statusDate.fieldName === StatusDateType.SHIPTOREAPPROVE)?.visible ?? false, [statusDates]);

    const sort: (currentOrders: Order[], currentActiveSort: string, currentSortDirection: 'asc' | 'desc') => Order[] = useCallback((currentOrders: Order[], currentActiveSort: string, currentSortDirection: 'asc' | 'desc') => {

        const defaultCompare: (a: Order, b: Order, multiplier: number) => number = (a: Order, b: Order, multiplier: number) => {

            const vesselCodeDiff: number = multiplier * (a.vesselCode ?? '').localeCompare(b.vesselCode ?? '');

            if (vesselCodeDiff !== 0) {
                return vesselCodeDiff;
            }

            const orderYearDiff: number = (-1) * multiplier * ((a.orderYear ?? 0) - (b.orderYear ?? 0)); //multiplied by -1 as this should be opposite of the vesselCode one

            if (orderYearDiff !== 0) {
                return orderYearDiff;
            }

            const orderNoDiff: number = (-1) * multiplier * ((a.orderNo ?? 0) - (b.orderNo ?? 0)); //multiplied by -1 as this should be opposite of the vesselCode one
        
            return orderNoDiff; //all these three should never be 0
        };

        const multiplier: number = currentSortDirection === 'asc' ? 1 : -1;

        if (currentActiveSort === 'orderNo') { //actually a special type of sorting that is used as default
            return [...currentOrders].sort((a: Order, b: Order) => defaultCompare(a, b, multiplier));
        } else if (currentActiveSort === 'vesselName') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const vesselNameDiff: number = multiplier * (a.vesselName ?? '').localeCompare(b.vesselName ?? '');

                if (vesselNameDiff !== 0) {
                    return vesselNameDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else if (currentActiveSort === 'shipOrderedDate') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const shipOrderedDateDiff: number = multiplier * ((a.shipOrderedDate?.getTime() ?? 0) - (b.shipOrderedDate?.getTime() ?? 0));

                if (shipOrderedDateDiff !== 0) {
                    return shipOrderedDateDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else if (currentActiveSort === 'subject') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const subjectDiff: number = multiplier * (a.subject ?? '').localeCompare(b.subject ?? '');

                if (subjectDiff !== 0) {
                    return subjectDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else if (currentActiveSort === 'department') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const departmentDiff: number = multiplier * (a.department ?? '').localeCompare(b.department ?? '');

                if (departmentDiff !== 0) {
                    return departmentDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else if (currentActiveSort === 'orderType') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const orderTypeDiff: number = multiplier * (a.orderType ?? '').localeCompare(b.orderType ?? '');

                if (orderTypeDiff !== 0) {
                    return orderTypeDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else if (currentActiveSort === 'priority') {
            return [...currentOrders].sort((a: Order, b: Order) => {

                const priorityDiff: number = multiplier * (a.priority ?? '').localeCompare(b.priority ?? '');

                if (priorityDiff !== 0) {
                    return priorityDiff;
                }
                
                return defaultCompare(a, b, multiplier);
            });
        } else {
            return currentOrders;
        }
    }, []);

    const reqApprovalOrders: Order[] = useMemo<Order[]>(() => orders.filter((order: Order) => {

        if (vesselIdsFiltering.length > 0 && !vesselIdsFiltering.includes(order.vesselId ?? -1)) { //as the filtering is currently so simple, it is just put in here
            return false;
        }

        if (order.shipOrderedDate === null) {
            return false;
        }

        if (order.postponedToDate !== null && order.postponedToDate > new Date()) {
            return false;
        }

        if (order.requisitionApprovedDate !== null) {
            return false;
        }

        if (order.rfqSentDate !== null) {
            return false;
        }

        if (order.awaitingApproval !== null) {
            return false;
        }

        if (order.approvedDate !== null) {
            return false;
        }

        if (order.officeOrderedDate !== null) {
            return false;
        }

        if (order.confirmedDate !== null) {
            return false;
        }

        if (order.partlyDeliveredDate !== null) {
            return false;
        }

        if (order.onboardDate !== null) {
            return false;
        }

        if (order.finishedDate !== null) {
            return false;
        }

        return true;
    }), [orders, vesselIdsFiltering]);

    const orderApprovalOrders: Order[] = useMemo<Order[]>(() => orders.filter((order: Order) => {

        if (vesselIdsFiltering.length > 0 && !vesselIdsFiltering.includes(order.vesselId ?? -1)) { //as the filtering is currently so simple, it is just put in here
            return false;
        }

        if (canApproveReadyOnly && order.awaitingApproval === null) {
            return false;
        }

        if (order.approvedDate !== null) {
            return false;
        }

        if (order.officeOrderedDate !== null) {
            return false;
        }

        if (order.finishedDate !== null) {
            return false;
        }

        if (order.postponedToDate !== null && order.postponedToDate > new Date()) {
            return false;
        }

        if (canShowReqApproval && order.requisitionApprovedDate === null) {
            return false;
        }

        return true;
    }), [canApproveReadyOnly, canShowReqApproval, orders, vesselIdsFiltering]);

    const orderElements: JSX.Element[] = useMemo<JSX.Element[]>(() => sort((orderMode === 'reqApproval' ? reqApprovalOrders : orderApprovalOrders), activeSort, sortDirection).map((order: Order) => {

        return (
            <div
                key={order.orderId}
                onClick={(): void => navigate('/orders/' + order.orderId)}
                style={{
                    cursor: 'pointer',
                    borderColor: currentTheme.palette.text.primary,
                    borderStyle: 'solid',
                    borderRadius: '5px',
                    borderWidth: '2px',
                    margin: '5px',
                    padding: '5px',
                }}
            >
                <table style={{
                    width: '100%',
                    tableLayout: 'fixed',
                }}>
                    <tbody>
                        <tr>
                            <td style={{
                                fontWeight: 'bold',
                                fontSize: '20px',
                                color: currentTheme.palette.text.primary,
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                {order.orderRef}
                            </td>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                                textAlign: 'right',
                                fontWeight: 'bold',
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                {order.vesselName}
                            </td>
                        </tr>
                        <tr>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                                paddingTop: '5px',
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                <span style={{fontWeight: 'bold'}}>Type:</span> {order.orderType}
                            </td>
                            <td style={{
                                color: order.getPriorityColor(settingsContext.theme),
                                textAlign: 'right',
                                paddingTop: '5px',
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                <span style={{fontWeight: 'bold'}}>Priority:</span> {order.priority}
                            </td>
                        </tr>
                        <tr>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                                paddingTop: '5px',
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                <span style={{fontWeight: 'bold'}}>Dep:</span> {order.department}
                            </td>
                            <td style={{
                                color: currentTheme.palette.text.primary,
                                textAlign: 'right',
                                paddingTop: '5px',
                                width: '50%',
                                wordWrap: 'break-word',
                            }}>
                                <span style={{fontWeight: 'bold'}}>Ship Ordered:</span> {datetimeToVisualString(order.shipOrderedDate, true, 0)}
                            </td>
                        </tr>
                        <tr>
                            <td 
                                colSpan={2}
                                style={{
                                    color: currentTheme.palette.text.primary,
                                    paddingTop: '5px',
                                    width: '50%',
                                    wordWrap: 'break-word',
                                }}
                            >
                                <span style={{fontWeight: 'bold'}}>Subject:</span> {order.subject}
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        );
    }), [activeSort, currentTheme.palette.text.primary, navigate, orderApprovalOrders, orderMode, reqApprovalOrders, settingsContext.theme, sort, sortDirection]);
    
    useEffect(() => {

        function updateSize(): void {
            setWindowSize([window.innerWidth, window.innerHeight]);
        }

        window.addEventListener('resize', updateSize);
        updateSize();
        return () => window.removeEventListener('resize', updateSize);
    }, []);

    useEffect(() => {

        let mounted: boolean = true;

        async function fetchOrders(): Promise<void> {

            setOrdersIsLoaded(false);

            const newOrders: Order[] = await apiContext.api?.getOrders(tokenContext.token) ?? [];

            if (!mounted) {
                return;
            }

            setOrders(newOrders);

            setOrdersIsLoaded(true);
        }

        void fetchOrders();

        return () => {
            mounted = false;
        };
    }, [apiContext.api, tokenContext.token, rerenderFlag]);

    useEffect(() => {

        let mounted: boolean = true;

        async function fetchStatusDates(): Promise<void> {

            setStatusDatesIsLoaded(false);

            const newStatusDates: StatusDate[] = await apiContext.api?.getStatusDates(tokenContext.token) ?? [];

            if (!mounted) {
                return;
            }

            setStatusDates(newStatusDates);

            setStatusDatesIsLoaded(true);
        }

        void fetchStatusDates();

        return () => {
            mounted = false;
        };
    }, [apiContext.api, tokenContext.token]);

    useEffect(() => {

        let mounted: boolean = true;

        async function fetchVessels(): Promise<void> {

            setVesselsIsLoaded(false);

            const newVessels: Vessel[] = await apiContext.api?.getVessels(tokenContext.token) ?? [];

            if (!mounted) {
                return;
            }
            
            setVessels(newVessels);

            setVesselsIsLoaded(true);
        }

        void fetchVessels();

        return () => {
            mounted = false;
        };
    }, [apiContext.api, tokenContext.token]);
    
    useEffect(() => {

        const sorting: [string, 'asc' | 'desc'] | null = localStorageContext.localStorageService.getOrderSorting();
        const filtering: number[] = localStorageContext.localStorageService.getOrderVesselFiltering();

        if (sorting !== null) {
            setActiveSort(sorting[0]);
            setSortDirection(sorting[1]);
        }

        setVesselIdsFiltering(filtering);
    }, [localStorageContext.localStorageService, localStorageRerenderFlag]);

    function handleCloseOrderInfoSubTab(): void {

        navigate('/orders');
    }

    function handleCloseVesselSortingDialog(success: boolean, newActiveSort: string, newSortDirection: 'asc' | 'desc'): void {

        if (success) {
            setActiveSort(newActiveSort);
            setSortDirection(newSortDirection);
            localStorageContext.localStorageService.setOrderSorting(newActiveSort, newSortDirection);
        }

        setShowVesselSortingDialog(false);
    }

    function handleCloseVesselFilteringDialog(success: boolean): void {

        if (success) {
            setLocalStorageRerenderFlag(new Date());
        }

        setShowVesselFilteringDialog(false);
    }

    return (
        <>
            {props.openedOrderId === null &&
                <div style={{
                    height: windowSize[1] - navBarHeight,
                    overflow: 'auto',
                    position: 'relative',
                }}>
                    <div style={{
                        textAlign: 'center',
                        padding: '25px 0px 10px 0px',
                    }}>
                        {canShowReqApproval &&
                            (
                                <ButtonGroup variant='outlined'>
                                    <Button 
                                        onClick={(): void => setOrderMode('reqApproval')}
                                        style={{
                                            minWidth: '120px',
                                            maxWidth: '120px',
                                            minHeight: '40px',
                                            maxHeight: '40px',
                                            lineHeight: 'normal',
                                            color: currentTheme.palette.text.primary,
                                            borderColor: currentTheme.palette.text.primary,
                                            backgroundColor: orderMode === 'reqApproval' ? '#888888' : undefined,
                                        }}
                                    >
                                        Req. Approval
                                    </Button>
                                    <Button 
                                        onClick={(): void => setOrderMode('orderApproval')}
                                        style={{
                                            minWidth: '120px',
                                            maxWidth: '120px',
                                            minHeight: '40px',
                                            maxHeight: '40px',
                                            lineHeight: 'normal',
                                            color: currentTheme.palette.text.primary,
                                            borderColor: currentTheme.palette.text.primary,
                                            backgroundColor: orderMode === 'orderApproval' ? '#888888' : undefined,
                                        }}
                                    >
                                        Order Approval
                                    </Button>
                                </ButtonGroup>
                            )
                        }
                        <IconButton 
                            edge='end' 
                            color='inherit' 
                            onClick={(): void => setShowVesselSortingDialog(true)}
                            style={{
                                marginLeft: '20px',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faSort}
                                color={currentTheme.palette.secondary.main}    
                            />
                        </IconButton>
                        <IconButton 
                            edge='end' 
                            color='inherit' 
                            onClick={(): void => setShowVesselFilteringDialog(true)}
                            style={{
                                marginLeft: '20px',
                            }}
                        >
                            <FontAwesomeIcon
                                icon={faFilter}
                                color={vesselIdsFiltering.length > 0 ? '#FFA500' : currentTheme.palette.secondary.main}    
                            />
                        </IconButton>
                    </div>
                    {orderElements.length > 0 
                        ? orderElements
                        : <p style={{
                            color: currentTheme.palette.text.primary,
                            textAlign: 'center',
                            margin: '40px 0px 80px 0px',
                        }}>
                            No orders to show
                        </p>
                    }

                    <LoadDataBackdrop
                        dataIsLoaded={ordersIsLoaded && statusDatesIsLoaded && vesselsIsLoaded}
                        zIndex={1500}
                    />

                    <VesselSortingDialog
                        controller={showVesselSortingDialog}
                        onClose={handleCloseVesselSortingDialog}
                        initialActiveSort={activeSort}
                        initialSortDirection={sortDirection}
                    />

                    <VesselFilteringDialog
                        controller={showVesselFilteringDialog}
                        onClose={handleCloseVesselFilteringDialog}
                        vessels={vessels}
                        initialSelectedVesselIds={vesselIdsFiltering}
                    />
                </div>
            }
            {props.openedOrderId !== null &&
                <OrderInfoSubTab
                    orderId={props.openedOrderId}
                    order={openedOrder}
                    closeSubTab={handleCloseOrderInfoSubTab}
                    canApproveReadyOnly={canApproveReadyOnly}
                    canShowReqApproval={canShowReqApproval}
                    canPostpone={canPostpone}
                    canRequestVesselToReapprove={canRequestVesselToReapprove}
                    setRerenderFlag={setRerenderFlag}
                />
            }
        </>
    );
}

export default OrdersTab;
